import { useEffect, useState, useRef } from 'react'
import api from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Play, Square, Search, Check } from 'lucide-react'

interface ElevenLabsVoice {
  id: string
  name: string
  category: string
  gender: string | null
  accent: string | null
  age: string | null
  description: string | null
  useCase: string | null
  previewUrl: string | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Current voice ID — either an alias (molly) or full Telnyx voice string.
  currentVoiceId: string
  // Active TTS model — used to construct the full Telnyx voice string on select.
  ttsModel: string
  // Callback invoked when admin picks a voice. Receives the FULL Telnyx voice
  // string (e.g. ElevenLabs.eleven_flash_v2.<id>) ready to drop into the
  // custom voice field.
  onSelect: (telnyxVoiceString: string, voice: ElevenLabsVoice) => void
}

export default function VoiceLibraryModal({ open, onOpenChange, currentVoiceId, ttsModel, onSelect }: Props) {
  const [voices, setVoices] = useState<ElevenLabsVoice[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [genderFilter, setGenderFilter] = useState<string | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!open || voices.length > 0) return
    setLoading(true)
    setError(null)
    api.get('/api/platform/elevenlabs/voices')
      .then(res => {
        setVoices(res.data?.data || [])
      })
      .catch(err => {
        setError(err?.response?.data?.error || err?.message || 'Failed to load voices')
      })
      .finally(() => setLoading(false))
  }, [open, voices.length])

  // Stop any playing audio when modal closes
  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      setPlayingId(null)
    }
  }, [open])

  const playPreview = (voice: ElevenLabsVoice) => {
    if (!voice.previewUrl) return
    if (playingId === voice.id) {
      audioRef.current?.pause()
      audioRef.current = null
      setPlayingId(null)
      return
    }
    if (audioRef.current) {
      audioRef.current.pause()
    }
    const audio = new Audio(voice.previewUrl)
    audio.onended = () => setPlayingId(null)
    audio.onerror = () => setPlayingId(null)
    audio.play().catch(() => setPlayingId(null))
    audioRef.current = audio
    setPlayingId(voice.id)
  }

  const handleSelect = (voice: ElevenLabsVoice) => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
      setPlayingId(null)
    }
    const telnyxString = `ElevenLabs.${ttsModel}.${voice.id}`
    onSelect(telnyxString, voice)
    onOpenChange(false)
  }

  // Determine which voice in the library matches the currently-selected voice
  // (so we can show a "currently selected" badge). Strip the prefix if it's a
  // full Telnyx voice string; otherwise compare against the bare ID.
  const currentBareId = (() => {
    if (!currentVoiceId) return null
    if (currentVoiceId.startsWith('ElevenLabs.')) {
      const parts = currentVoiceId.split('.')
      return parts[parts.length - 1] || null
    }
    return null
  })()

  const filtered = voices.filter(v => {
    if (genderFilter && v.gender !== genderFilter) return false
    if (search) {
      const q = search.toLowerCase()
      const haystack = [v.name, v.description, v.useCase, v.accent, v.age].filter(Boolean).join(' ').toLowerCase()
      if (!haystack.includes(q)) return false
    }
    return true
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Browse ElevenLabs Voices</DialogTitle>
          <DialogDescription>
            All ElevenLabs premade voices. Click play to preview, click a voice to select. Selecting builds the full
            Telnyx voice string with your active TTS model (<code className="text-[11px] bg-muted px-1 rounded">{ttsModel}</code>).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mt-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Filter by name, accent, age, use case…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-7 h-8 text-xs"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button variant={genderFilter === null ? 'default' : 'outline'} size="sm" onClick={() => setGenderFilter(null)} className="h-8 text-xs">All</Button>
            <Button variant={genderFilter === 'female' ? 'default' : 'outline'} size="sm" onClick={() => setGenderFilter('female')} className="h-8 text-xs">Female</Button>
            <Button variant={genderFilter === 'male' ? 'default' : 'outline'} size="sm" onClick={() => setGenderFilter('male')} className="h-8 text-xs">Male</Button>
            <Button variant={genderFilter === 'neutral' ? 'default' : 'outline'} size="sm" onClick={() => setGenderFilter('neutral')} className="h-8 text-xs">Neutral</Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mt-2 -mx-6 px-6">
          {loading && <div className="py-8 text-center text-sm text-muted-foreground">Loading voice library…</div>}
          {error && <div className="py-8 text-center text-sm text-destructive">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">No voices match your filters.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filtered.map(voice => {
              const isCurrent = currentBareId === voice.id
              const isPlaying = playingId === voice.id
              return (
                <div
                  key={voice.id}
                  className={`group border rounded-md p-3 hover:border-primary/60 hover:bg-muted/30 cursor-pointer transition-colors ${isCurrent ? 'border-primary bg-primary/5' : ''}`}
                  onClick={() => handleSelect(voice)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{voice.name}</span>
                    {isCurrent && <Check className="h-3.5 w-3.5 text-primary" />}
                    <div className="flex-1" />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation()
                        playPreview(voice)
                      }}
                      disabled={!voice.previewUrl}
                      title={voice.previewUrl ? (isPlaying ? 'Stop' : 'Play preview') : 'No preview available'}
                    >
                      {isPlaying ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-1">
                    {voice.gender && <Badge variant="secondary" className="text-[10px] capitalize">{voice.gender}</Badge>}
                    {voice.accent && <Badge variant="secondary" className="text-[10px] capitalize">{voice.accent}</Badge>}
                    {voice.age && <Badge variant="secondary" className="text-[10px] capitalize">{voice.age.replace(/_/g, ' ')}</Badge>}
                    {voice.useCase && <Badge variant="outline" className="text-[10px] capitalize">{voice.useCase.replace(/_/g, ' ')}</Badge>}
                  </div>
                  {voice.description && (
                    <p className="text-[11px] text-muted-foreground line-clamp-2">{voice.description}</p>
                  )}
                  <p className="text-[9px] font-mono text-muted-foreground mt-1 truncate">{voice.id}</p>
                </div>
              )
            })}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground pt-2 border-t">
          {filtered.length} of {voices.length} voices · Click a voice to select. Voice strings are constructed as{' '}
          <code className="text-[10px] bg-muted px-1 rounded">ElevenLabs.{ttsModel}.&lt;id&gt;</code> using your active TTS Model.
        </p>
      </DialogContent>
    </Dialog>
  )
}
